import type { Messages } from "../types";

export const messages: Messages = {
  shared: {
    title: "Fielmann Kundenkonto",
    tabs: {
      login: "Einloggen",
      signup: "Konto erstellen",
      ariaLabel: "Authentifizierung",
    },
    languageSwitcher: {
      label: "Sprache auswählen",
    },
    password: {
      show: "Passwort anzeigen",
      hide: "Passwort verbergen",
    },
    header: {
      logoAriaLabel: "Fielmann",
    },
    footer: {
      ariaLabel: "Fußzeile",
      customerService: "Kundenservice",
      legalNotice: "Impressum",
      dataProtection: "Datenschutz",
    },
    loading: {
      ariaLabel: "Einen Augenblick, bitte...",
      content: "Inhalte werden geladen.",
    },
    genericError:
      "Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
  },
  login: {
    subtitle: "Logge dich mit deiner E-Mail und deinem Passwort ein.",
    form: {
      emailLabel: "E-Mail *",
      passwordLabel: "Passwort *",
      forgotPassword: "Passwort vergessen?",
      submitButton: "Einloggen",
      credentialError: "E-Mail und Passwort passen nicht zusammen.",
    },
  },
  signup: {
    subtitle: {
      before: "Nutze die ",
      highlight: "Vorteile",
      after: " und erstelle dein Fielmann Kundenkonto.",
    },
    benefits: {
      popoverHeading: "Deine Vorteile:",
      closeLabel: "Schließen",
      items: {
        orders:
          "Alle Fielmann Bestellungen inklusive Bestellstatus auf einen Blick.",
        visionValues: "Sehwerte für die nächste Bestellung übernehmen.",
        appointments: "Termine buchen und verwalten.",
        reorders: "Nachbestellungen ganz einfach online erledigen.",
      },
    },
    form: {
      firstNameLabel: "Vorname *",
      lastNameLabel: "Nachname *",
      emailLabel: "E-Mail *",
      passwordLabel: "Passwort *",
      passwordRepeatLabel: "Passwort wiederholen *",
      submitButton: "Konto erstellen",
      errorTitle: "Das Konto konnte nicht erstellt werden.",
      errorMessage:
        "Bitte überprüfe deine Eingaben oder versuche es später noch einmal.",
    },
    consents: {
      requiredError: "Bitte stimme der erforderlichen Einwilligung zu.",
      unavailableError:
        "Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
      required: {
        headline: "Zustimmung zum Kundenkonto",
        subline:
          "Damit wir dir alle Inhalte in deinem Kundenkonto anzeigen können (z.B. Käufe oder Termine), brauchen wir deine Zustimmung.",
      },
      optional: {
        headline: "Möchtest du personalisierte Werbung erhalten?",
        subline:
          "Du hast die Wahl: Mit der Zustimmung erhältst du auf dich zugeschnittene Angebote und Informationen. Ohne Zustimmung nutzt du dein Kundenkonto ohne personalisierte Werbung.",
      },
    },
  },
  emailVerification: {
    subtitle: "Du kannst dich nicht einloggen?",
    alertTitle: "Bestätige erst deine E-Mail-Adresse.",
    alertBody:
      "Bitte bestätige zuerst deine E-Mail-Adresse, um dein Fielmann Konto zu aktivieren.",
    alertSpamHint:
      "Du findest die E-Mail nicht? Schau auch im Spam-Ordner nach.",
    resendButton: "E-Mail erneut anfordern",
    backToLogin: "Zurück zum Einloggen",
  },
  resetPasswordRequest: {
    subtitle: "Hast du dein Passwort vergessen?",
    body: "Gib bitte die E-Mail-Adresse ein, mit der du dein Konto erstellt hast. Wir schicken dir einen Link, mit dem du dein Passwort ganz einfach zurücksetzen kannst.",
    emailLabel: "E-Mail *",
    submitButton: "Link anfordern",
    backToLogin: "Zurück zum Einloggen",
    banner: {
      closeAria: "Schließen",
      rateLimit: {
        title: "E-Mail zu häufig angefordert",
        body: "Bitte überprüfe dein Postfach und den Spam-Ordner, um die E-Mail zum Zurücksetzen deines Passworts zu finden.",
        waitHint:
          "Du findest sie trotzdem nicht? Warte bitte einen Moment – möglicherweise trifft die E-Mail noch ein.",
      },
      error: {
        title: "Senden fehlgeschlagen",
        body: "Bitte versuche es später noch einmal.",
      },
    },
  },
  resetPasswordEmail: {
    success: {
      heading: "E-Mail zum Zurücksetzen gesendet.",
      body: "Wir haben dir eine E-Mail zum Zurücksetzen deines Passworts geschickt. Bitte schau in deinem Postfach nach.",
      spamHint:
        "Du kannst die E-Mail nicht finden? Schau am besten auch im Spam-Ordner nach.",
    },
    restart: {
      hint: "Falls du die E-Mail weiterhin nicht findest, kannst du den Vorgang erneut starten. Vielleicht hast du eine andere E-Mail-Adresse verwendet.",
      button: "Erneut versuchen",
    },
    error: {
      heading: "Senden fehlgeschlagen",
      body: "Bitte versuche es später noch einmal.",
      retryButton: "Noch einmal versuchen",
      rateLimitBody:
        "Zu viele Versuche. Bitte warte einige Minuten, bevor du es erneut versuchst.",
    },
  },
  setPassword: {
    subtitle: "Du kannst jetzt dein Passwort festlegen.",
    passwordLabel: "Passwort *",
    passwordRepeatLabel: "Passwort wiederholen *",
    submitButton: "Passwort speichern",
    backToLogin: "Zum Einloggen",
    successTitle: "Dein Passwort wurde erfolgreich gespeichert.",
    successRedirectBody: "Du wirst jetzt zu unserer Startseite weitergeleitet.",
    successRedirectLoginHint:
      "Dort kannst du dich mit deinem neuen Passwort einloggen.",
    successWaitHint: "Einen Moment, bitte.",
  },
  resetPasswordError: {
    expired: {
      heading: "Dieser Link ist abgelaufen.",
      body: "Der Link zum Zurücksetzen deines Passworts ist nicht mehr gültig. Gehe zurück zur Startseite und starte den Vorgang über den Login mit „Passwort vergessen?“ erneut.",
    },
    used: {
      heading: "Dieser Link wurde bereits verwendet.",
      body: "Mit diesem Link wurde das Passwort bereits zurückgesetzt. Falls du dein Passwort erneut ändern möchtest, gehe zurück zur Startseite und starte den Vorgang über den Login mit „Passwort vergessen?“ erneut.",
    },
    generic: {
      heading: "Dieser Link ist ungültig.",
      body: "Der Link konnte nicht verwendet werden. Gehe zurück zur Startseite und starte den Vorgang über den Login mit „Passwort vergessen?“ erneut.",
    },
    backToHome: "Zurück zur Startseite",
  },
  toasts: {
    missingRequiredFields:
      "Bitte fülle alle noch fehlenden Pflichtfelder aus, um fortzufahren.",
  },
  validation: {
    passwordRequired: "Bitte gib ein Passwort ein.",
    passwordRepeatRequired: "Bitte gib das Passwort erneut ein.",
    firstNameTooLong: "Der Vorname darf nicht länger als 30 Zeichen sein.",
    firstNameRequired: "Bitte gib einen Vornamen ein.",
    firstNameInvalidFormat: "Bitte gib einen gültigen Vornamen ein.",
    lastNameTooLong: "Der Nachname darf nicht länger als 30 Zeichen sein.",
    lastNameRequired: "Bitte gib einen Nachnamen ein.",
    lastNameInvalidFormat: "Bitte gib einen gültigen Nachnamen ein.",
    emailTooLong: "Die E-Mail-Adresse ist zu lang.",
    emailRequired: "Bitte gib eine E-Mail-Adresse ein.",
    emailInvalidFormat: "Bitte gib eine gültige E-Mail-Adresse ein.",
    passwordMatchesEmail:
      "Das Passwort darf nicht mit der E-Mail-Adresse übereinstimmen.",
    passwordMismatch: "Die Passwörter stimmen nicht überein.",
  },
  passwordRequirements: {
    minimumLength: "Mindestens 10 Zeichen",
    mixedCase: "Mindestens ein Groß- und ein Kleinbuchstabe",
    hasNumber: "Mindestens eine Zahl",
    specialCharactersAllowed: "Sonderzeichen sind erlaubt (z. B. !@#$%^&*)",
  },
};
